import { getSentences } from './formatting';
import { getVenues } from './venues';
import { Supplier } from '../types';
import { getApolloClient } from '../connectors/apollo';
import {
  LocalisedSupplierCountDocument,
  LocalisedSupplierCountQuery,
  LocalisedSupplierCountQueryVariables,
  PublicSupplierReviewsDocument,
  PublicSupplierReviewsQuery,
  PublicSupplierReviewsQueryVariables,
  SimpleSupplierSearchDocument,
  SimpleSupplierSearchQuery,
  SimpleSupplierSearchQueryVariables,
  SimpleSupplierSearchSort,
} from '../graphql/codegen';

// lat/lon come through as strings from the SEO admin and IP lookup
const toCoordinate = (value?: string | number | null): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;

  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate : undefined;
};

export interface GetSuppliersParams {
  page?: number;
  limit?: number;
  name?: string;
  postcode?: string;
  lat?: string | number;
  lon?: string | number;
  date?: string;
  tier1?: string;
  tier2?: string[];
  tags?: string[];
  dietary?: string[];
  sort?: string;
  exclude?: string[];
  mandatoryTags?: string[];
}

export const getSuppliers = async ({
  page = 1,
  limit = 12,
  lat,
  lon,
  sort,
  ...filters
}: GetSuppliersParams): Promise<{ results: Supplier[]; count: number }> => {
  const latitude = toCoordinate(lat);
  const longitude = toCoordinate(lon);
  const hasLocation = latitude !== undefined && longitude !== undefined;

  const { data } = await getApolloClient().query<
    SimpleSupplierSearchQuery,
    SimpleSupplierSearchQueryVariables
  >({
    query: SimpleSupplierSearchDocument,
    variables: {
      filters: {
        ...filters,
        page,
        limit,
        ...(hasLocation && { lat: latitude, lon: longitude }),
        ...(sort === 'distance' && {
          sort: SimpleSupplierSearchSort.Distance,
        }),
      },
    },
  });

  return {
    // Copied because Apollo freezes results and callers append backfilled suppliers.
    // Generated types mark every field nullable, the page components already guard for missing values
    results: ([
      ...(data.simpleSupplierSearch?.suppliers ?? []),
    ] as unknown) as Supplier[],
    count: data.simpleSupplierSearch?.totalResults ?? 0,
  };
};

export const getLocalisedSupplierCount = async (
  { lat, lon }: { lat?: string | number; lon?: string | number },
  tier1: string
): Promise<number> => {
  const { data } = await getApolloClient().query<
    LocalisedSupplierCountQuery,
    LocalisedSupplierCountQueryVariables
  >({
    query: LocalisedSupplierCountDocument,
    variables: {
      tier1,
      lat: toCoordinate(lat),
      lon: toCoordinate(lon),
    },
  });

  return data.localisedSupplierCount;
};

export const getSupplierDescriptor = supplier => {
  return supplier?.tagline;
};

type PublicSupplierReview = PublicSupplierReviewsQuery['publicSupplierReviews']['reviews'][number];

export const getReviews = async (
  supplierUuid: string,
  page = 1,
  limit = 5
): Promise<PublicSupplierReview[]> =>
  getApolloClient()
    .query<PublicSupplierReviewsQuery, PublicSupplierReviewsQueryVariables>({
      query: PublicSupplierReviewsDocument,
      variables: { supplierUuid, page, limit },
    })
    // A new array because Apollo freezes results and the caller shifts the first review off.
    // Reviews without a public comment have nothing to quote, so they're dropped here
    .then(({ data }) =>
      (data.publicSupplierReviews?.reviews ?? []).filter(review =>
        review.fields.publicComment?.trim()
      )
    )
    .catch(() => []);

const formatReview = (supplier, review, imageIndex = 0) => ({
  supplierName: supplier?.name,
  supplierSlug: supplier?.slug,
  eventType:
    review?.externalEvent?.eventType || review?.booking?.type || 'Event',
  image: supplier?.public?.images?.[imageIndex],
  author:
    review?.externalEvent?.userFullName ||
    `${review?.booking?.customer?.firstName} ${review?.booking?.customer?.lastName}`,
  rating: review?.ratings?.overall?.toFixed(1) || 5.0,
  quote: getSentences(2)(review?.fields.publicComment),
});

export const getReviewsFromSuppliers = async (suppliers, count) => {
  // Filter array of suppliers to ones that actually have reviews
  const suppliersWithReviews = suppliers.filter(
    ({ external }) => !!Number(external?.numReviews)
  );

  if (!suppliersWithReviews.length) return null;

  let reviews = [];

  // We want to show reviews for different suppliers
  // If only one supplier has reviews, we push them all here and use as a backup
  const backupReviews = [];

  // Loop over suppliers until the reviews array matches the count arg
  for (let i = 0; reviews.length < count; i++) {
    const supplier = suppliersWithReviews[i];

    if (supplier) {
      const supplierReviews = await getReviews(supplier.uuid);

      // Skip suppliers whose reviews couldn't be loaded
      if (!supplierReviews.length) continue;

      // Add the first review for each supplier to the reviews array
      reviews.push(formatReview(supplier, supplierReviews[0]));

      // Remove first item to avoid duplicate in backups
      supplierReviews.shift();

      // Add the remainder of the reviews to backup
      supplierReviews.forEach((review, i) =>
        backupReviews.push(formatReview(supplier, review, i + 1))
      );
    } else {
      if (backupReviews.length) {
        const numberOfBackupsRequired = count - reviews.length;

        reviews = [
          ...reviews,
          ...backupReviews.splice(0, numberOfBackupsRequired),
        ];
      }

      break;
    }
  }

  return reviews;
};

export const getOfflineSuppliers = async ({ lat, lon }) => {
  try {
    const offlineSuppliers = await getVenues({ lat, lon });

    return offlineSuppliers;
  } catch (error) {
    return [];
  }
};

// A function that transforms offline supplier to match the shape of online suppliers
export const getTransformOfflineSupplier = offlineSupplier => {
  return {
    average_rating: 0,
    categoryTier1: '',
    dietary: [],
    distance: offlineSupplier.distance * 1609.34, //in meters
    external: {
      hygieneRating: '',
      iZettleStaffName: null,
      foodSafetyAgencyId: null,
    },
    name: offlineSupplier.DisplayName,
    public: {
      images: [
        {
          filename: '',
          key: '',
          url: offlineSupplier?.Images?.[0]
            ? offlineSupplier.Images[0].url
            : '',
        },
      ],
      social: null,
      video_url: '',
      description: '',
      profilePicture: {
        filename: '',
        key: '',
        url: offlineSupplier?.Images?.[0] ? offlineSupplier.Images[0].url : '',
      },
    },
    slug: '',
    state: 'offline',
    tagline: '',
    tier2: [],
    updated: '',
    uuid: '',
  };
};
